/**
 * The console's only road to schedule-bit: the public HTTP API, v1.
 *
 * Every call answers with an `Answer`, never a throw: a success with its
 * value (and the journal head when the act committed), or the API's own
 * problem+json, kept whole so the console can show a refusal in the API's
 * words. A network failure is a problem too, with status 0.
 *
 * The key goes in the Authorization header of requests to `base` and
 * nowhere else. The one exception to the header is the watch, which a
 * browser opens with a ticket; see `watchUrl`.
 */

// -- what the API returns ------------------------------------------------------------------

/** The engine's reason for a 409, as the API sends it. */
export interface Refusal {
  readonly by: string;
  readonly conflictsWith?: readonly string[];
  readonly slots?: string;
  readonly [field: string]: unknown;
}

/** RFC 9457 problem+json, with the API's extra fields. */
export interface Problem {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail: string;
  readonly refusal?: Refusal;
  /** A local time the zone repeats or skips: the UTC instants it may name. */
  readonly candidates?: ReadonlyArray<{ readonly occurrence: 'earlier' | 'later'; readonly instant: string; readonly date: string; readonly hour?: number; readonly slot: number }>;
  readonly zone?: string;
  readonly local?: readonly string[];
  readonly fault?: Record<string, unknown>;
  readonly leg?: number;
  readonly requestId?: string;
}

export type Answer<T> =
  | { readonly ok: true; readonly value: T; readonly head: string | null }
  | { readonly ok: false; readonly problem: Problem };

export interface Preset { readonly id: string; readonly position: string; readonly leafSlotBits: number }
export interface WorldEntry { readonly name: string; readonly preset: string; readonly createdAt: number }
export interface World {
  readonly name: string;
  readonly globalName: string;
  readonly preset: string;
  readonly leafSlotBits: number;
  readonly allowDisplace: boolean;
  readonly tieBreak: string;
  readonly seats: number;
  readonly occupiers: number;
  readonly relays: number;
  readonly seq: number;
  readonly head: string;
}
export type OccupierKind = 'holder' | 'event' | 'series' | 'foreign';
export interface Occupier {
  readonly id: number;
  readonly kind: OccupierKind;
  readonly name: string;
  readonly rank: number;
  readonly pinned: boolean;
  readonly holder: string | null;
  readonly keeper?: boolean;
}

/** A place slots live: a date (UTC), plus the hour on a minute World. */
export interface At { readonly date: string; readonly hour?: number }
/** A position in local time: a zone, the local day (or hour), and which of a repeated time. */
export interface LocalPlace { readonly zone: string; readonly at: string; readonly occurrence?: 'earlier' | 'later' }
/** Where a door act lands: a UTC position, or a local reading the API resolves. */
export type Place = At | LocalPlace;
export interface Moment extends At { readonly slot: number }
/** A list of slot indices, or a hex mask. */
export type Slots = readonly number[] | string;
/** An occupier by its id, or by its unique name. */
export type Who = number | string;

export interface Offered { readonly parametric: number; readonly coordinates: number[]; readonly offered: string; readonly slots: number[] }
export interface Holding {
  readonly occupier: Occupier;
  readonly held: string;
  readonly heldSlots: number[];
  readonly soft: string;
  readonly softSlots: number[];
}
export interface Displaced {
  readonly occupier: string;
  readonly slots: number[];
  readonly offered: { parametric: number; coordinates: number[]; slots: number[] } | null;
}
export interface Receipt {
  readonly wrote: string;
  readonly slots: number[];
  readonly displaced: Displaced[];
  readonly commitSeq: number;
  readonly head: string;
}
export interface JournalEntry { readonly occupier: string; readonly role: string; readonly key: number; readonly set: string; readonly clear: string }
export interface Commit {
  readonly seq: number;
  readonly tick: number;
  readonly actor: string | null;
  readonly kind: string;
  readonly ref: string | null;
  readonly prevHash: string;
  readonly hash: string;
  readonly entries: JournalEntry[];
}
export interface Journal { readonly head: string; readonly seq: number; readonly commits: Commit[] }

export type PlaneRole = 'held' | 'intent' | 'proposed';
export interface Plane {
  readonly occupier: string;
  readonly id: number;
  readonly kind: OccupierKind;
  readonly role: PlaneRole;
  readonly mask: string;
}
/** One position's bits: what is offered, and every occupier plane with a bit set. */
export interface PlanesPosition {
  readonly parametric: number;
  readonly coordinates: number[];
  readonly key: number;
  readonly offered: string;
  readonly planes: Plane[];
}
export interface Planes {
  readonly seq: number;
  readonly head: string;
  readonly leafSlotBits: number;
  readonly seats: number;
  readonly positions: PlanesPosition[];
}

export type Standing = 'unasked' | 'asked' | 'held' | 'refused' | 'withdrawn' | 'released';
export type RelayVerb = 'ask' | 'sync' | 'withdraw' | 'delegate';
export interface Piece {
  readonly parametric: number;
  readonly coordinates: number[];
  readonly slots: number[];
  readonly token: string;
  readonly standing: Standing;
  readonly restsOn: { seq: number; act: string; by: string } | null;
}
export interface LegSpec { readonly hand: string; readonly length: number; readonly gapAfter?: number; readonly after?: number[] }
export interface Leg extends LegSpec {
  readonly leg: number;
  readonly standing: Standing;
  readonly pieces: Piece[];
  readonly because: Refusal | null;
}
export interface Relay {
  readonly name: string;
  readonly standing: Standing | 'mixed';
  readonly legs: Leg[];
  readonly shortfalls: { leg: number; hand: string; by: number }[];
  readonly head: string;
  readonly possible: RelayVerb[];
  readonly told?: unknown;
}
export interface Windows {
  readonly starts: { parametric: number; coordinates: number[]; slot: number }[];
  readonly blockedAt: unknown;
}
export type RelayAct =
  | { readonly act: 'ask'; readonly start: Moment }
  | { readonly act: 'sync' }
  | { readonly act: 'withdraw' }
  | { readonly act: 'delegate'; readonly leg: number; readonly to: string };

export type AskState = 'open' | 'accepted' | 'rejected' | 'withdrawn' | 'released';
export interface Ask {
  readonly token: string;
  readonly from: string;
  readonly parametric: number;
  readonly coordinates: number[];
  readonly slots: number[];
  readonly state: AskState | null;
  readonly held: number[];
  readonly because: Refusal | null;
}
export type Verdict = 'accept' | 'reject' | 'takeBack' | 'moveTo';

export interface Webhook { readonly id: string; readonly url: string; readonly events: string[]; readonly createdAt: number }
/** A webhook as it is made: the only time its secret is shown. */
export interface NewWebhook extends Webhook { readonly secret: string }

export interface Ticket { readonly ticket: string; readonly expiresAt: number }

export interface SignupConfig { readonly turnstileSitekey: string | null; readonly available: boolean }
export interface SignupPending { readonly status: 'pending'; readonly detail: string; readonly expiresInSeconds: number }
/** What a verified sign-up makes: the workspace, and its owner key with the only showing of its secret. */
export interface SignedUp {
  readonly workspace: string;
  readonly plan: string;
  readonly ownerEmail: string;
  readonly key: { readonly id: string; readonly label: string; readonly scopes: string[]; readonly secret: string };
}

// -- the client --------------------------------------------------------------------------------

export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** A base URL as the console keeps it: no trailing slash, and nothing after the host but a path. */
export const normalizeBase = (base: string): string => base.trim().replace(/\/+$/, '');

const unreachable = (detail: string): Problem => ({
  type: 'about:blank', title: 'Unreachable', status: 0, detail,
});

/** Query string for a position (and extras), skipping what is unset. */
const query = (params: Record<string, string | number | undefined>): string => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
};

const seg = encodeURIComponent;

export class Api {
  readonly base: string;

  constructor(base: string, private readonly key: string, private readonly fetcher: Fetch = (i, o) => fetch(i, o)) {
    this.base = normalizeBase(base);
  }

  /** One request. Only this method touches the network, and only `base`. */
  async request<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<Answer<T>> {
    let response: Response;
    try {
      response = await this.fetcher(`${this.base}${path}`, {
        method,
        headers: {
          // Sign-up is had without a key, so a client made for it has none to send.
          ...(this.key ? { authorization: `Bearer ${this.key}` } : {}),
          ...(body === undefined ? {} : { 'content-type': 'application/json' }),
          ...headers,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (error) {
      // A browser reports a CORS refusal the same way as a dead host: a TypeError and no response.
      return { ok: false, problem: unreachable(`could not reach ${this.base} (${String(error)}); check the URL, and that this page's origin is in the API's CONSOLE_ORIGINS`) };
    }
    const text = await response.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = null;
    }
    if (!response.ok) {
      const p = (parsed ?? {}) as Partial<Problem>;
      return {
        ok: false,
        problem: {
          ...p,
          type: p.type ?? 'about:blank',
          title: p.title ?? response.statusText ?? 'Error',
          status: response.status,
          detail: p.detail ?? (text || `the API answered ${response.status}`),
        },
      };
    }
    return { ok: true, value: parsed as T, head: response.headers.get('x-journal-head') };
  }

  private get = <T>(path: string) => this.request<T>('GET', path);
  private post = <T>(path: string, body: unknown = {}, idempotencyKey?: string) =>
    this.request<T>('POST', path, body, idempotencyKey ? { 'idempotency-key': idempotencyKey } : {});
  private w = (world: string) => `/v1/worlds/${seg(world)}`;

  // -- open ---------------------------------------------------------------------------------
  health() { return this.get<{ ok: boolean }>('/v1/health'); }
  presets() { return this.get<{ presets: Preset[] }>('/v1/presets'); }

  // -- sign-up (no key) -----------------------------------------------------------------------
  signupConfig() { return this.get<SignupConfig>('/v1/signup/config'); }
  signup(body: { email: string; workspace: string; turnstileToken: string }) { return this.post<SignupPending>('/v1/signup', body); }
  verifySignup(token: string) { return this.post<SignedUp>('/v1/signup/verify', { token }); }

  // -- Worlds -------------------------------------------------------------------------------
  worlds() { return this.get<{ worlds: WorldEntry[] }>('/v1/worlds'); }
  createWorld(body: { name: string; preset?: string; allowDisplace?: boolean; tieBreak?: string }) {
    return this.post<World>('/v1/worlds', body);
  }
  world(name: string) { return this.get<World>(this.w(name)); }

  // -- occupiers ----------------------------------------------------------------------------
  occupiers(world: string) { return this.get<{ occupiers: Occupier[] }>(`${this.w(world)}/occupiers`); }
  declare(world: string, body: { name: string; kind?: 'holder' | 'event' | 'series'; holder?: string; rank?: number; pinned?: boolean }) {
    return this.post<{ occupier: Occupier }>(`${this.w(world)}/occupiers`, body);
  }
  capacity(world: string, seats: number) { return this.post<{ seats: number }>(`${this.w(world)}/capacity`, { seats }); }

  // -- the door -----------------------------------------------------------------------------
  claim(world: string, body: Place & { who: Who; slots: Slots; rank?: number; soft?: boolean; until?: number }, idempotencyKey?: string) {
    return this.post<Receipt>(`${this.w(world)}/claims`, body, idempotencyKey);
  }
  explain(world: string, body: At & { who: string; slots: Slots; rank?: number }) {
    return this.post<{ admissible: true }>(`${this.w(world)}/claims:explain`, body);
  }
  release(world: string, body: Place & { who: Who; slots: Slots }) { return this.post<Receipt>(`${this.w(world)}/releases`, body); }
  confirm(world: string, body: At & { who: string; slots: Slots }) { return this.post<Receipt>(`${this.w(world)}/confirmations`, body); }
  giveUp(world: string, body: At & { who: string; slots: Slots }) { return this.post<Receipt>(`${this.w(world)}/give-ups`, body); }

  // -- reading ------------------------------------------------------------------------------
  offered(world: string, at: At) { return this.get<Offered>(`${this.w(world)}/offered${query({ ...at })}`); }
  holdings(world: string, who: string, at: At) { return this.get<Holding>(`${this.w(world)}/holdings${query({ who, ...at })}`); }
  journal(world: string, since = 0, limit = 100) { return this.get<Journal>(`${this.w(world)}/journal${query({ since, limit })}`); }
  /** Every plane's bits over `positions` consecutive positions from `at` (days, or hours on a minute World); at most 32. */
  planes(world: string, at: At, positions: number) { return this.get<Planes>(`${this.w(world)}/planes${query({ ...at, positions })}`); }

  // -- relays -------------------------------------------------------------------------------
  hands(world: string) { return this.get<{ hands: { hand: string; root: string }[] }>(`${this.w(world)}/hands`); }
  takeIn(world: string, hand: string) { return this.post<Record<string, unknown>>(`${this.w(world)}/hands`, { hand }); }
  relays(world: string) { return this.get<{ relays: Relay[] }>(`${this.w(world)}/relays`); }
  relay(world: string, name: string) { return this.get<Relay>(`${this.w(world)}/relays/${seg(name)}`); }
  layRelay(world: string, name: string, legs: readonly LegSpec[]) { return this.post<Relay>(`${this.w(world)}/relays`, { name, legs }); }
  windows(world: string, relay: string, at: At, positions = 1) {
    return this.get<Windows>(`${this.w(world)}/relays/${seg(relay)}/windows${query({ ...at, positions })}`);
  }
  act(world: string, relay: string, act: RelayAct) { return this.post<Relay>(`${this.w(world)}/relays/${seg(relay)}/acts`, act); }

  // -- asks ---------------------------------------------------------------------------------
  asks(world: string) { return this.get<{ asks: Ask[] }>(`${this.w(world)}/asks`); }
  answer(world: string, token: string, verdict: Verdict, slots?: Slots) {
    return this.post<{ answer: Record<string, unknown> }>(`${this.w(world)}/asks/${seg(token)}/answer`,
      verdict === 'moveTo' ? { verdict, slots } : { verdict });
  }

  // -- webhooks -----------------------------------------------------------------------------
  webhooks() { return this.get<{ webhooks: Webhook[] }>('/v1/webhooks'); }
  createWebhook(url: string, events: readonly string[] = ['*']) { return this.post<NewWebhook>('/v1/webhooks', { url, events }); }
  deleteWebhook(id: string) { return this.request<{ deleted: string }>('DELETE', `/v1/webhooks/${seg(id)}`); }

  // -- watching -----------------------------------------------------------------------------
  watchTicket(world: string) { return this.post<Ticket>(`${this.w(world)}/watch-tickets`); }
  /** Where a browser opens the watch: the ticket goes in the URL, since a WebSocket carries no header. */
  watchUrl(world: string, ticket: string): string {
    return `${this.base.replace(/^http/, 'ws')}${this.w(world)}/watch?ticket=${seg(ticket)}`;
  }
}

/** A problem as one line a person can read: the API's detail, and the refusal's reason when there is one. */
export const describeProblem = (p: Problem): string =>
  p.refusal ? `${p.detail} (refused: ${p.refusal.by})` : p.status === 0 ? p.detail : `${p.status} ${p.title}: ${p.detail}`;
